import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {Cloud, Email, Close} from '@mui/icons-material';
import { InviteUserRequest } from '../../types';
import { userManagementService } from '../../services';
import { useNotification } from '../../context/NotificationContext';

interface InviteUserDialogProps {
  open: boolean;
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  organizationId,
  organizationName,
  onClose,
  onSuccess
}) => {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<InviteUserRequest>({
    name: '',
    email: '',
    organizationId: organizationId,
    role: 'reader'
  });

  const handleInputChange = (field: keyof InviteUserRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      showNotification(t('admin:messages.allFieldsRequired'), 'error');
      return;
    }

    if (!formData.email.includes('@')) {
      showNotification(t('admin:messages.emailInvalid'), 'error');
      return;
    }

    setLoading(true);
    try {
      const result = await userManagementService.inviteUser({
        ...formData,
        organizationId: organizationId
      });

      if (result.status === 'success') {
        if (result.azure_details?.invitation_status === 'user_exists') {
          showNotification(
            t('admin:inviteUserDialog.successExisting', { email: formData.email }), 
            'success'
          );
        } else {
          showNotification(
            t('admin:inviteUserDialog.successNew', { email: formData.email }), 
            'success'
          );
        }
        
        setFormData({
          name: '',
          email: '',
          organizationId: organizationId,
          role: 'reader'
        });
        onSuccess();
        onClose();
      } else {
        showNotification(`${t('admin:inviteUserDialog.error')}: ${result.message}`, 'error');
      }
    } catch (error: any) {
      console.error('Error inviting user:', error);
      showNotification(
        error.response?.data?.detail || t('admin:inviteUserDialog.error'), 
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        organizationId: organizationId,
        role: 'reader'
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon color="inherit" />
          <Typography variant="h6">
            {t('admin:inviteUserDialog.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {organizationName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box >
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Cloud fontSize="small" />
              <Typography variant="body2" dangerouslySetInnerHTML={{ __html: t('admin:inviteUserDialog.azureNote') }} />
            </Box>
          </Alert>
        </Box>

        <TextField
          fullWidth
          label="Vollständiger Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          margin="normal"
          required
          disabled={loading}
          placeholder="z.B. Max Mustermann"
          data-testid="admin-invite-user-name-input"
        />

        <TextField
          fullWidth
          label="E-Mail-Adresse"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          margin="normal"
          required
          disabled={loading}
          placeholder="z.B. max.mustermann@unternehmen.de"
          data-testid="admin-invite-user-email-input"
        />

        {/* Rollen-Auswahl */}
        <FormControl fullWidth margin="normal" size="small" disabled={loading}>
          <InputLabel>{t('admin:inviteUserDialog.roleLabel')}</InputLabel>
          <Select
            label={t('admin:inviteUserDialog.roleLabel')}
            value={formData.role}
            onChange={(e) => handleInputChange('role', e.target.value as InviteUserRequest['role'])}
            data-testid="admin-invite-user-role-select"
          >
            <MenuItem value="reader">{t('admin:inviteUserDialog.roleUser')}</MenuItem>
            <MenuItem value="qs">{t('admin:inviteUserDialog.roleQs')}</MenuItem>
            <MenuItem value="super_qs">{t('admin:inviteUserDialog.roleSuperQs')}</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
        <span
        style={{ 
          display: 'flex', 
          flex: 1, 
          justifyContent: 'flex-end', 
          gap: 4, 
          color: 'primary.dark',
          textTransform: 'none', 
          fontWeight: 500, 
          fontSize: '1rem'
        }}
      >
        <Box 
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} 
          onClick={handleClose}
          data-testid="admin-invite-user-dialog-cancel-button"
        >
          <Close sx={{ fontSize: '1.4rem' }}/>
          {t('admin:inviteUserDialog.cancel')}
        </Box>
      </span>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim() || !formData.email.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
          data-testid="admin-invite-user-submit-button"
        >
          {loading ? t('admin:inviteUserDialog.loading') : t('admin:inviteUserDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog; 